/**
 * Attio Import — Review step.
 *
 * Shows the engine's proposed property mappings grouped by entity type, with
 * a per-row override between mapping to an existing property, creating a new
 * one, or skipping it. Presentational only — the wizard owns fetching the
 * proposals and holding the override state.
 */

import type { Key } from 'react';
import { memo, useCallback, useMemo } from 'react';

import { EmptyState } from '@/lib/base/empty-state';
import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import { Table } from '@/lib/base/table/table';
import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import { cx } from '@/utils/cx';

import type {
  AttioMappingAction,
  AttioMappingProposal,
  DiscoveredObjectType,
} from './api';

/** `actionByKey` / `onActionChange` key for one proposal row. */
// eslint-disable-next-line react-refresh/only-export-components -- shared helper reused by AttioImportWizard, not a component
export function reviewRowKey(entityType: string, rawKey: string): string {
  return `${entityType}::${rawKey}`;
}

export interface AttioReviewStepProps {
  /** null when the proposals request failed to load. */
  proposalsByEntity: Record<string, AttioMappingProposal[]> | null;
  loadError: boolean;
  /**
   * Per-entity-type flag: true when the backend capped that entity type's
   * proposals and dropped the rest. null when proposals haven't loaded.
   */
  truncatedByEntity: Record<string, boolean> | null;
  /**
   * Records scanned per entity type — the denominator for each proposal's
   * populated_count. null when proposals haven't loaded.
   */
  totalRecordsByEntity: Record<string, number> | null;
  /** Keyed by reviewRowKey(entity_type_name, raw_key). */
  actionByKey: Record<string, AttioMappingAction>;
  onActionChange: (key: string, action: AttioMappingAction) => void;
  /**
   * The custom object types discovered during Configure ({ label,
   * entity_type_name }). Their user-facing labels are threaded through so this
   * step's headings and notes match Configure — without them a discovered type
   * like `ahoy_lf_ticket` falls back to the humanized entity name ("Lf
   * Tickets") instead of its configured label ("Ticket"). Built-in types are
   * absent here and resolve through getTypeLabelByName as before.
   */
  discoveredTypes?: DiscoveredObjectType[];
}

function describeMapping(
  proposal: AttioMappingProposal,
  hasExistingTarget: boolean,
  isSkipped: boolean
): string {
  if (hasExistingTarget) {
    return `Matches your existing "${proposal.target_property_name}" property`;
  }
  // Conditional tense when the row is skipped: "we'll create X" next to an Action
  // of "Skip" reads as a contradiction — nothing is going to be created. This
  // describes what the row would do if the user turns it back on.
  return isSkipped
    ? `No matching property yet – would create "${proposal.label}"`
    : `No matching property yet – we'll create "${proposal.label}"`;
}

/**
 * "3 of 285 have this set", or "Nothing to import" at zero.
 *
 * Returns null when the backend didn't send a count (an older response), so an
 * absent count renders nothing rather than a misleading "0 of 0".
 */
function describeCoverage(
  proposal: AttioMappingProposal,
  totalRecords: number | undefined
): string | null {
  if (proposal.populated_count === undefined || totalRecords === undefined) {
    return null;
  }
  if (proposal.populated_count === 0) {
    return 'Nothing to import';
  }
  return `${proposal.populated_count} of ${totalRecords} have this set`;
}

function describeAction(
  action: AttioMappingAction,
  proposal: AttioMappingProposal
): string {
  switch (action) {
    case 'map_existing':
      return `Map to ${proposal.target_property_name}`;
    case 'create_new':
      return 'Create new property';
    case 'skip':
      return 'Skip';
  }
}

interface ReviewRowProps {
  entityType: string;
  proposal: AttioMappingProposal;
  totalRecords: number | undefined;
  selectedAction: AttioMappingAction;
  onActionChange: (key: string, action: AttioMappingAction) => void;
}

const ReviewRow = memo(function ReviewRow({
  entityType,
  proposal,
  totalRecords,
  selectedAction,
  onActionChange,
}: ReviewRowProps) {
  const rowKey = reviewRowKey(entityType, proposal.raw_key);
  const coverage = describeCoverage(proposal, totalRecords);
  const isEmpty = proposal.populated_count === 0;

  // Map-to-existing is only offered when the engine actually matched an existing
  // property. `matched_existing` is the engine's own finding — do NOT re-derive
  // it from proposed_action (a zero-count row is `skip` either way) or from
  // target_property_name (which holds a name we'd MINT on the create branch).
  // Inferring it claimed a match on every skipped row against an org that had no
  // custom properties at all, and offered a "Map to org_x" that pointed nowhere.
  //
  // Falls back to the action for a response predating the field, which is correct
  // for every non-skipped row and conservative for the rest.
  const hasExistingTarget =
    proposal.matched_existing ?? proposal.proposed_action === 'map_existing';
  const availableActions: AttioMappingAction[] = hasExistingTarget
    ? ['map_existing', 'create_new', 'skip']
    : ['create_new', 'skip'];

  const actionItems = availableActions.map(action => ({
    id: action,
    label: describeAction(action, proposal),
  }));

  const handleActionChange = useCallback(
    (key: Key | null) => {
      if (key == null) {
        return;
      }
      onActionChange(rowKey, key as AttioMappingAction);
    },
    [rowKey, onActionChange]
  );

  return (
    <Table.Row id={rowKey}>
      <Table.Cell>
        <span className='text-sm font-medium text-primary'>
          {proposal.label}
        </span>
      </Table.Cell>
      <Table.Cell>
        <span className='text-sm text-tertiary'>
          {describeMapping(
            proposal,
            hasExistingTarget,
            selectedAction === 'skip'
          )}
        </span>
      </Table.Cell>
      <Table.Cell>
        <span
          className={cx(
            'text-sm',
            isEmpty ? 'text-quaternary' : 'text-tertiary'
          )}
        >
          {coverage ?? '—'}
        </span>
      </Table.Cell>
      <Table.Cell>
        <Select
          aria-label={`Action for ${proposal.label}`}
          size='xs'
          value={selectedAction}
          onChange={handleActionChange}
          items={actionItems}
        >
          {item => <SelectItem {...item} />}
        </Select>
      </Table.Cell>
    </Table.Row>
  );
});

export function AttioReviewStep({
  proposalsByEntity,
  loadError,
  truncatedByEntity,
  totalRecordsByEntity,
  actionByKey,
  onActionChange,
  discoveredTypes = [],
}: AttioReviewStepProps) {
  // Configured labels for discovered custom types, keyed by entity_type_name.
  // Built-ins aren't in here — they resolve through getTypeLabelByName.
  const discoveredLabelByEntityType = useMemo(() => {
    const map: Record<string, string> = {};
    for (const type of discoveredTypes) {
      map[type.entity_type_name] = type.label;
    }
    return map;
  }, [discoveredTypes]);

  // Discovery's label is already the plural noun ("Invoices", "Tickets") — it
  // comes straight from Attio's plural_noun. So a custom type uses its label
  // directly for plural headings; running it through the pluralizer would
  // double-pluralize it ("Invoiceses"). Built-in types have no discovered label
  // and go through getTypeLabelByName, which does the pluralizing for them.
  const pluralLabel = useCallback(
    (entityType: string): string =>
      discoveredLabelByEntityType[entityType] ??
      getTypeLabelByName(entityType, { plural: true }),
    [discoveredLabelByEntityType]
  );

  if (loadError) {
    return (
      <EmptyState
        title="We couldn't load the proposed property mappings."
        description="You can continue and we'll map properties automatically."
      />
    );
  }

  const sections = proposalsByEntity
    ? Object.entries(proposalsByEntity).filter(
        ([, proposals]) => proposals.length > 0
      )
    : [];

  // Entity types the engine computed proposals for but found nothing needing
  // review — every one of their fields maps to a system property
  // automatically. Surfaced as a note so a selected type (e.g. Deals) never
  // silently vanishes from this step.
  const fullyMappedTypes = proposalsByEntity
    ? Object.entries(proposalsByEntity)
        .filter(([, proposals]) => proposals.length === 0)
        .map(([entityType]) => entityType)
    : [];

  if (sections.length === 0 && fullyMappedTypes.length === 0) {
    return (
      <EmptyState
        title='No custom properties to review.'
        description='No properties need mapping for the selected record types.'
      />
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      {sections.map(([entityType, proposals]) => {
        const entityLabel = pluralLabel(entityType);
        const isTruncated = truncatedByEntity?.[entityType] ?? false;
        return (
          <div key={entityType} className='flex flex-col gap-2'>
            <h3 className='text-sm font-semibold text-primary'>
              {entityLabel}
            </h3>
            {isTruncated && (
              <p className='text-sm text-[var(--color-text-warning-primary)]'>
                Showing the first {proposals.length} properties for{' '}
                {entityLabel}. Additional properties were not loaded for review.
              </p>
            )}
            <div className='overflow-x-auto rounded-lg border border-[var(--color-border-secondary)]'>
              <Table aria-label={`${entityLabel} property mappings`} size='sm'>
                <Table.Header>
                  <Table.Head label='Field' isRowHeader />
                  <Table.Head label='Mapping' />
                  <Table.Head label='Records with a value' />
                  <Table.Head label='Action' />
                </Table.Header>
                <Table.Body>
                  {proposals.map(proposal => (
                    <ReviewRow
                      key={reviewRowKey(entityType, proposal.raw_key)}
                      entityType={entityType}
                      proposal={proposal}
                      totalRecords={totalRecordsByEntity?.[entityType]}
                      selectedAction={
                        actionByKey[
                          reviewRowKey(entityType, proposal.raw_key)
                        ] ?? proposal.proposed_action
                      }
                      onActionChange={onActionChange}
                    />
                  ))}
                </Table.Body>
              </Table>
            </div>
          </div>
        );
      })}
      {fullyMappedTypes.length > 0 && (
        <div className='flex flex-col gap-1'>
          {fullyMappedTypes.map(entityType => (
            <p key={entityType} className='text-sm text-tertiary'>
              All properties for {pluralLabel(entityType).toLowerCase()} were
              mapped to system properties.
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
